import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Controlled editor for a passthrough assignment's payout breakdown.
 *
 * Props:
 *  - payoutActions: [{ action, value, note }]  — rows. Empty value rows are filtered server-side.
 *  - setPayoutActions: setter
 *  - headlinePayout: string  — optional "up to 45%" headline
 *  - setHeadlinePayout: setter
 *  - splitPct: string  — publisher split
 *  - setSplitPct: setter  — pass null to hide the split input (used when split is edited elsewhere)
 *  - idPrefix: string  — prefix for data-testids so multiple editors can coexist
 */
export default function PayoutBreakdownEditor({
  payoutActions,
  setPayoutActions,
  headlinePayout,
  setHeadlinePayout,
  splitPct,
  setSplitPct,
  idPrefix = "pt",
}) {
  const updateRow = (i, patch) => {
    const next = [...payoutActions];
    next[i] = { ...next[i], ...patch };
    setPayoutActions(next);
  };

  const removeRow = (i) => {
    if (payoutActions.length === 1) {
      setPayoutActions([{ action: "", value: "", note: "" }]);
    } else {
      setPayoutActions(payoutActions.filter((_, idx) => idx !== i));
    }
  };

  const addRow = () =>
    setPayoutActions([...payoutActions, { action: "", value: "", note: "" }]);

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-editor`}>
      <div className="border border-slate-200 rounded-md p-3 bg-white space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-700 font-bold">
            Payout Breakdown
            <span className="ml-1.5 text-rose-600">*</span>
          </Label>
          <span className="text-[10px] text-slate-400">
            formats: 35%, 35%-45%, $0.10, $4-$16
          </span>
        </div>

        {payoutActions.map((row, i) => (
          <div
            key={i}
            className="grid grid-cols-12 gap-2 items-center"
            data-testid={`${idPrefix}-row-${i}`}
          >
            <Input
              placeholder="Action (e.g. Online Sale)"
              value={row.action || ""}
              onChange={(e) => updateRow(i, { action: e.target.value })}
              className="col-span-5 h-8 text-sm"
              data-testid={`${idPrefix}-action-${i}`}
            />
            <Input
              placeholder="Value (e.g. 35%-45%)"
              value={row.value || ""}
              onChange={(e) => updateRow(i, { value: e.target.value })}
              className="col-span-4 h-8 text-sm font-mono"
              data-testid={`${idPrefix}-value-${i}`}
            />
            <Input
              placeholder="Note"
              value={row.note || ""}
              onChange={(e) => updateRow(i, { note: e.target.value })}
              className="col-span-2 h-8 text-[11px]"
            />
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="col-span-1 text-slate-400 hover:text-rose-600 text-lg leading-none"
              title="Remove row"
              data-testid={`${idPrefix}-remove-${i}`}
            >
              ×
            </button>
          </div>
        ))}

        <button
          type="button"
          onClick={addRow}
          className="text-[11px] text-[#002FA7] hover:underline font-semibold"
          data-testid={`${idPrefix}-add-btn`}
        >
          + Add another action
        </button>

        <div className="pt-2 border-t border-slate-100">
          <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
            Headline (optional — shown to pub as the primary number)
          </Label>
          <Input
            placeholder="e.g. 'up to 45%' — leave blank to auto-build from rows above"
            value={headlinePayout || ""}
            onChange={(e) => setHeadlinePayout(e.target.value)}
            className="h-8 text-sm mt-1"
            data-testid={`${idPrefix}-headline`}
          />
        </div>
      </div>

      {setSplitPct && (
        <div className="grid grid-cols-12 gap-3 items-end">
          <div className="col-span-12 sm:col-span-3 space-y-1">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              Split (pub %)
            </Label>
            <Input
              type="number"
              step="1"
              value={splitPct ?? ""}
              onChange={(e) => setSplitPct(e.target.value)}
              className="h-9 text-sm"
              data-testid={`${idPrefix}-split`}
              placeholder="100"
            />
          </div>
          <div className="col-span-12 sm:col-span-9 text-[11px] text-slate-500">
            Pub earns <span className="font-mono">network payout × split / 100</span> on whatever
            the offer actually pays per conversion.
          </div>
        </div>
      )}
    </div>
  );
}
